// src/pages/SaminaBook.js

import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Container, Row, Col, Form, Button, Card } from 'react-bootstrap';
// Firebase Importları Eklendi
import { db } from '../config/firebaseConfig';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

const useFadeInEffect = () => {
  const animatedElements = useRef([]);

  useEffect(() => {
    const elementsToObserve = animatedElements.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible', 'fade-in-premium');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    );

    if (elementsToObserve) {
      elementsToObserve.forEach((el) => {
        if (el) observer.observe(el);
      });
    }

    return () => {
      if (elementsToObserve) {
        elementsToObserve.forEach((el) => {
          if (el) observer.unobserve(el);
        });
      }
    };
  }, []);

  const addToRefs = (el) => {
    if (el && !animatedElements.current.includes(el)) {
      animatedElements.current.push(el);
    }
  };

  return addToRefs;
};

const SaminaBook = () => {
  const addToRefs = useFadeInEffect();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: '', msg: '' });

  useEffect(() => {
    window.scrollTo(0, 0); // Sayfa açıldığında en üste kaydırır
  }, []);

  // Firebase Veritabanına Kayıt Fonksiyonu
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', msg: '' });

    try {
      // Veriyi Firestore'daki 'bookSubscriptions' tablosuna kaydet
      await addDoc(collection(db, "bookSubscriptions"), {
        email: email,
        bookName: "I Can Speak English",
        createdAt: serverTimestamp(),
      });

      setStatus({ type: 'success', msg: "Thank you! You've successfully subscribed to our updates." });
      setEmail('');
    } catch (error) {
      console.error("Error saving email: ", error);
      setStatus({ type: 'error', msg: "Something went wrong. Please try again." });
    }
    setLoading(false);
  };

  return (
    <>
      <Helmet>
        <title>I Can Speak English | Coming Soon</title>
        <meta name="description" content="A gentle and supportive tool for children growing up in dual-language homes." />
      </Helmet>

      <style>{`
        /* Sayfa Özel Stilleri */
        .book-hero-bg {
          background-color: #fef9f1;
          background-image: radial-gradient(#fde8d4 1.5px, transparent 1.5px);
          background-size: 30px 30px;
          padding: 80px 0;
        }
        .classy-heading { font-family: 'Playfair Display', serif; color: #2c3e50; }
        .text-reading { font-size: 1.15rem; line-height: 1.9; color: #4a5568; }
        
        .fade-in-premium { animation: fadeInPremium 1s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes fadeInPremium {
          from { opacity: 0; transform: translateY(40px); filter: blur(5px); }
          to { opacity: 1; transform: translateY(0); filter: blur(0); }
        }
        .invisible-start { opacity: 0; }

        .book-cover-shadow {
          box-shadow: -15px 20px 40px rgba(0,0,0,0.15);
          border-radius: 4px 16px 16px 4px;
          transition: transform 0.4s ease;
        }
        .book-cover-shadow:hover {
          transform: translateY(-10px) rotateY(-5deg);
        }

        .subscribe-card {
          border: none;
          border-radius: 24px;
          background: #ffffff;
          box-shadow: 0 20px 50px rgba(0,0,0,0.06);
          position: relative;
          overflow: hidden;
        }
        .subscribe-card::before {
          content: ''; position: absolute; top: 0; left: 0; width: 6px; height: 100%; background-color: #fca5a5;
        }
      `}</style>

      <main>
        <section className="book-hero-bg">
          <Container>
            <Row className="align-items-center mb-5">
              
              {/* SOL: Ana Kitap Kapağı */}
              <Col lg={5} className="mb-5 mb-lg-0 text-center invisible-start" ref={addToRefs}>
                <img 
                  src="/images/samina/5.png" 
                  alt="I can speak English Book Cover" 
                  className="img-fluid book-cover-shadow border border-light"
                  style={{ maxHeight: '600px', objectFit: 'contain' }}
                />
              </Col>

              {/* SAĞ: Metin ve Email Formu */}
              <Col lg={7} className="ps-lg-5 invisible-start" ref={addToRefs}>
                <div className="mb-4">
                  <span className="badge bg-warning text-dark px-3 py-2 rounded-pill text-uppercase fw-bold tracking-wider mb-3 shadow-sm">
                    Coming Soon
                  </span>
                  <h1 className="display-3 classy-heading fw-bold mb-3">I can speak English</h1>
                  <h4 className="text-muted mb-4" style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
                    Written by Samina Uddin
                  </h4>
                </div>

                <div className="text-reading mb-5">
                  <p className="fw-bold fs-5" style={{ color: '#1a365d' }}>
                    This book is coming soon, and we are so excited to share it with you!
                  </p>
                  <p>
                    Created with care and purpose, this story is designed as a gentle and supportive tool for children growing up in dual-language homes. It speaks to those everyday moments where one language is heard at home, and another is learned in the world outside.
                  </p>
                  <p>
                    At its heart, this story celebrates the beauty of language as a bridge, not a barrier. It encourages children to feel confident in who they are, proud of the languages they carry, and open to learning from others. 
                  </p>
                  <p>
                    Through storytelling, we hope to show that friendships can be built through curiosity, kindness, and the sharing of our words and worlds. We cannot wait to place this story into the hands of families around the world.
                  </p>
                </div>

                {/* EMAIL ABONELİK FORMU */}
                <Card className="subscribe-card p-4 p-md-5 invisible-start" ref={addToRefs}>
                  <h4 className="classy-heading fw-bold mb-3">Join the Journey</h4>
                  <p className="text-muted mb-4">
                    Share your email address to keep updated about the launch of this book and freebies we will share.
                  </p>
                  
                  {/* Başarı veya Hata Mesajı Gösterimi */}
                  {status.msg && (
                    <div className={`alert ${status.type === 'success' ? 'alert-success' : 'alert-danger'} border-0 fw-bold rounded-4`} role="alert">
                      {status.type === 'success' ? <i className="bi bi-check-circle-fill me-2"></i> : <i className="bi bi-exclamation-triangle-fill me-2"></i>}
                      {status.msg}
                    </div>
                  )}

                  {!status.msg && (
                    <Form onSubmit={handleSubmit} className="d-flex flex-column flex-md-row gap-3">
                      <Form.Control 
                        type="email" 
                        placeholder="Enter your email address" 
                        className="py-3 px-4 rounded-pill border-1 shadow-sm"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        style={{ fontSize: '1.1rem' }}
                      />
                      <Button type="submit" variant="dark" className="py-3 px-5 rounded-pill fw-bold shadow-sm" disabled={loading} style={{ whiteSpace: 'nowrap' }}>
                        {loading ? 'Sending...' : 'Notify Me'}
                      </Button>
                    </Form>
                  )}
                </Card>
              </Col>
            </Row>

            {/* ÇİFT DİLLİ VERSİYONLAR (GALLERY) */}
            <div className="pt-5 mt-5 border-top border-warning text-center invisible-start" ref={addToRefs}>
              <h3 className="classy-heading mb-3">Available in Multiple Dual-Language Editions</h3>
              <p className="text-muted mb-5">Celebrating diverse cultures and language bridges around the world.</p>
              
              <Row className="g-4 justify-content-center">
                {[1, 2, 3, 4].map((num) => (
                  <Col xs={6} md={3} key={num}>
                    <img 
                      src={`/images/samina/${num}.png`} 
                      alt={`Edition ${num}`} 
                      className="img-fluid rounded-3 shadow-sm border border-light"
                      style={{ transition: 'transform 0.3s', cursor: 'pointer' }}
                      onMouseOver={(e) => e.target.style.transform='scale(1.05)'} 
                      onMouseOut={(e) => e.target.style.transform='scale(1)'}
                    />
                  </Col>
                ))}
              </Row>
            </div>

          </Container>
        </section>
      </main>
    </>
  );
};

export default SaminaBook;